import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormControl } from '@angular/forms';
import { BehaviorSubject, Subject } from 'rxjs';
import { PaginationLimit } from 'src/app/core/constants/dropdown-contants';
import { LoaderService } from 'src/app/core/services/loader/loader.service';
import { UniversityService } from 'src/app/core/services/university/university.service';
import { CacheUniversityService } from './services/cache-university.service';

@Component({
  selector: 'app-university-dropdown',
  templateUrl: './university.component.html',
  styleUrls: ['./university.component.scss']
})
export class UniversityComponent implements OnInit, OnChanges {
  @Input() control: FormControl = new FormControl(null);
  @Input() label: string = 'University';
  @Input() placeholder: string = 'Select University';
  @Input() selectedUniversity: string | string[] = '';
  @Input() showAllUniversity: boolean = false;
  @Input() multiple?: boolean = false;
  @Input() showClear?: boolean = false;
  @Output() search: EventEmitter<string> = new EventEmitter<string>();
  @Output() nextOffsetUniversity: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() universityDetails: EventEmitter<any> = new EventEmitter<any>();
  @Output() selectionChange: EventEmitter<any> = new EventEmitter<any>();

  universityList: any[] = [];
  paginationLimit: number = PaginationLimit.university;
  totalUniversityCount: number = 0;
  universityOffset: number = PaginationLimit.university;
  universityPagination = {
    skip: 0,
    limit: PaginationLimit.university
  };
  universityFilter: FormControl = new FormControl('');
  private _unsubscribeAll: Subject<string> = new Subject();
  isLoading: boolean = false;
  searchValue: BehaviorSubject<string> = new BehaviorSubject('');

  constructor(
    private _loaderService: LoaderService,
    private _universityService: UniversityService,
    private _universityCacheService: CacheUniversityService
  ) { }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['selectedUniversity']?.currentValue) {
      if (this.universityList.length === 0) {
        this.getUniversity();
      } else {
        const selectedValues = this.multiple ? 
          (Array.isArray(this.selectedUniversity) ? this.selectedUniversity : [this.selectedUniversity]) :
          [this.selectedUniversity];
        
        selectedValues.forEach((id: any) => {
          if (id) this.checkUniversityExists(id);
        });
      }
    }
  }

  async ngOnInit() {
    if (this.multiple && !(this.control.value instanceof Array)) {
      this.control.setValue([]);
    }
    await this.getUniversity();
  }

  async getUniversity() {
    await this.loadUniversities();
    if (this.selectedUniversity) {
      const selectedValues = this.multiple ? 
        (Array.isArray(this.selectedUniversity) ? this.selectedUniversity : [this.selectedUniversity]) :
        [this.selectedUniversity];
      
      selectedValues.forEach((id: any) => {
        if (id) this.checkUniversityExists(id);
      });
    }
  }

  private async loadUniversities(filter: string = ''): Promise<void> {
    if (filter) {
      this.universityPagination = {
        skip: 0,
        limit: PaginationLimit.university
      }
      this.universityList = [];
    }
    try {
      this.isLoading = true;
      const universityData: { data: any[], total: number } = 
        await this._universityCacheService.fetchUniversities(this.universityPagination, filter);

      const universityList = universityData.data.map((v: any) => ({ 
        id: v.id, 
        name: v.name 
      }));

      this.universityList = [...this.universityList, ...universityList];
      if (this.showAllUniversity) {
        this.universityList.unshift({
          id: '',
          name: 'All University'
        });
      }
      
      // Ensure uniqueness
      this.universityList = Array.from(
        new Map(this.universityList.map(item => [item.id, item])).values()
      );
      
      this.totalUniversityCount = universityData.total;
    } catch (error) {
      console.error('Error loading universities:', error);
    } finally {
      this.isLoading = false;
    }
  }

  private checkUniversityExists(universityId: string): void {
    if (!this.universityList.some((u) => u.id === universityId)) {
      this._universityCacheService.getUniversityById(universityId).subscribe({
        next: (university) => {
          if (university && university?.statusCode === 200 && university?.data) {
            this.universityList = [
              ...this.universityList,
              { id: university?.data.id, name: university?.data.name }
            ];
          } else {
            console.warn(`University with ID ${universityId} not found.`);
          }
        },
        error: (err) => console.error('Error fetching university by ID:', err)
      });
    }
  }

  getNextUniversity(): void {
    if (this.searchValue?.value) return;
    if (this.universityPagination.skip < this.totalUniversityCount) {
      this.universityPagination.skip += this.paginationLimit;
      this.loadUniversities(this.universityFilter.value);
    }
  }

  async filterUniversity(filter: string) {
    this.searchValue.next(filter);
    this.universityList = [];
    await this.loadUniversities(filter);
    
    const currentValue = this.control.value;
    if (this.multiple && Array.isArray(currentValue)) {
      currentValue.forEach(id => {
        if (id) this.checkUniversityExists(id);
      });
    } else if (currentValue) {
      this.checkUniversityExists(currentValue);
    }
  }

  onSelectionChange(event: any) {
    const selectedValue = event.value; // Extract the value from the event object
    
    const value = this.multiple ? 
      (Array.isArray(selectedValue) ? selectedValue : [selectedValue]) : 
      selectedValue;
    
    this.control.setValue(value);
    this.selectionChange.emit(event);
  }
}